// src/ConsumerApp/Screens/ProductsList/components/TabletFiltersModal.js

import React, { useState, useEffect, useMemo } from 'react';
import X from '../../../../assets/icons/X.svg';

export default function TabletFiltersModal({
  visible = false,
  onClose,
  filterConfig = [],
  appliedFilters = [],
  onApply,
}) {
  const [draftFilters, setDraftFilters] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (visible) {
      setDraftFilters([...appliedFilters]);
      setSearchQuery('');
      if (filterConfig.length > 0) {
        // Default to first category if none active or current active not in config
        if (!activeCategoryId || !filterConfig.some((c) => c.id === activeCategoryId)) {
          setActiveCategoryId(filterConfig[0].id);
        }
      }
    }
  }, [visible, appliedFilters, filterConfig]);

  const activeCategory = useMemo(() => {
    return (
      filterConfig.find((c) => c.id === activeCategoryId) ||
      (filterConfig.length > 0 ? filterConfig[0] : null)
    );
  }, [activeCategoryId, filterConfig]);

  const filteredOptions = useMemo(() => {
    if (!activeCategory?.options) return [];
    if (!searchQuery.trim()) return activeCategory.options;
    return activeCategory.options.filter((opt) =>
      opt.label.toLowerCase().includes(searchQuery.toLowerCase().trim())
    );
  }, [activeCategory, searchQuery]);

  if (!visible) return null;

  const handleToggleOption = (category, option) => {
    if (!category || !option) return;
    setDraftFilters((prev) => {
      const exists = prev.some((f) => f.optionId === option.id);
      if (exists) {
        return prev.filter((f) => f.optionId !== option.id);
      }
      return [
        ...prev,
        {
          categoryId: category.id,
          categoryLabel: category.label,
          optionId: option.id,
          label: option.label,
        },
      ];
    });
  };

  const handleClear = () => {
    setDraftFilters([]);
  };

  const handleApplyCommit = () => {
    onApply(draftFilters);
    onClose();
  };

  return (
    <div className="bb-filters-overlay" onClick={onClose}>
      <style>{`
        .bb-filters-overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(15, 23, 42, 0.6);
          z-index: 999999;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(3px);
          padding: 16px;
          box-sizing: border-box;
        }

        .bb-filters-modal-card {
          background-color: #FFFFFF;
          border-radius: 16px;
          width: 100%;
          max-width: 620px;
          height: 75vh;
          max-height: 640px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 24px 48px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
          box-sizing: border-box;
        }

        .bb-filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #E2E8F0;
          background-color: #FFFFFF;
        }

        .bb-filters-search-wrap {
          padding: 10px 16px;
          border-bottom: 1px solid #E2E8F0;
          background-color: #F8FAFC;
        }

        .bb-filters-search-input {
          width: 100%;
          height: 38px;
          border-radius: 10px;
          border: 1px solid #CBD5E1;
          padding: 0 12px;
          font-size: 13px;
          color: #1E293B;
          background-color: #FFFFFF;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.15s ease;
        }

        .bb-filters-search-input:focus {
          border-color: #E9001E;
        }

        .bb-filters-body {
          display: grid;
          grid-template-columns: 200px 1fr;
          flex: 1;
          min-height: 0;
          overflow: hidden;
        }

        .bb-filters-cat-rail {
          background-color: #F8FAFC;
          border-right: 1px solid #E2E8F0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .bb-filters-cat-tab {
          padding: 14px 16px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #F1F5F9;
          gap: 8px;
          transition: background-color 0.15s ease;
          user-select: none;
        }

        .bb-filters-cat-tab.active {
          background-color: #FFFFFF;
          border-left: 3.5px solid #E9001E;
          font-weight: 700;
        }

        .bb-filters-cat-name {
          font-size: 13px;
          color: #1E293B;
          line-height: 16px;
        }

        .bb-filters-cat-count {
          font-size: 11px;
          font-weight: 800;
          color: #FFFFFF;
          background-color: #E9001E;
          padding: 1px 6px;
          border-radius: 999px;
          flex-shrink: 0;
        }

        .bb-filters-options-pane {
          padding: 16px 20px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .bb-filters-option-item {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          padding: 4px 0;
          user-select: none;
        }

        .bb-filters-checkbox-box {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid #CBD5E1;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #FFFFFF;
          flex-shrink: 0;
          transition: all 0.15s ease;
        }

        .bb-filters-checkbox-box.checked {
          border-color: #E9001E;
          background-color: #E9001E;
        }

        .bb-filters-option-label {
          font-size: 13.5px;
          color: #334155;
          font-weight: 500;
          line-height: 18px;
        }

        .bb-filters-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 20px;
          border-top: 1px solid #E2E8F0;
          background-color: #FFFFFF;
        }

        .bb-filters-empty-notice {
          padding: 40px 10px;
          text-align: center;
          color: #94A3B8;
          font-size: 13px;
        }

        /* ── Mobile Viewport: Bottom Sheet Transformation (<= 640px) ── */
        @media (max-width: 640px) {
          .bb-filters-overlay {
            padding: 0;
            align-items: flex-end;
          }

          .bb-filters-modal-card {
            max-width: 100vw;
            height: 82vh;
            max-height: 82vh;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            border-top-left-radius: 20px;
            border-top-right-radius: 20px;
            animation: slideUpModal 0.22s cubic-bezier(0.16, 1, 0.3, 1);
          }

          @keyframes slideUpModal {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }

          .bb-filters-body {
            grid-template-columns: 140px 1fr;
          }

          .bb-filters-cat-tab {
            padding: 12px 10px;
          }

          .bb-filters-cat-name {
            font-size: 12px;
          }

          .bb-filters-options-pane {
            padding: 14px 14px;
          }
        }
      `}</style>

      <div className="bb-filters-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bb-filters-header">
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0F172A' }}>Filters</h3>
            <span style={{ fontSize: 12, color: '#64748B', marginTop: 2, display: 'block' }}>
              {draftFilters.length} {draftFilters.length === 1 ? 'filter selected' : 'filters selected'}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: '#F1F5F9', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <img src={X} alt="close" style={{ width: 14, height: 14 }} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="bb-filters-search-wrap">
          <input
            type="text"
            className="bb-filters-search-input"
            placeholder={`Search ${activeCategory?.label || 'options'}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* 2-Column Responsive Body */}
        <div className="bb-filters-body">
          {/* Categories Rail */}
          <div className="bb-filters-cat-rail">
            {filterConfig.map((cat) => {
              const isSelected = activeCategory?.id === cat.id;
              const selectedCount = draftFilters.filter((f) => f.categoryId === cat.id).length;

              return (
                <div
                  key={cat.id}
                  className={`bb-filters-cat-tab ${isSelected ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategoryId(cat.id);
                    setSearchQuery('');
                  }}
                >
                  <span className="bb-filters-cat-name">{cat.label}</span>
                  {selectedCount > 0 && (
                    <span className="bb-filters-cat-count">{selectedCount}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Options Pane */}
          <div className="bb-filters-options-pane">
            {filteredOptions.length === 0 ? (
              <div className="bb-filters-empty-notice">
                {searchQuery ? `No options matching "${searchQuery}"` : 'No filter options available'}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isChecked = draftFilters.some((f) => f.optionId === option.id);

                return (
                  <label
                    key={option.id}
                    className="bb-filters-option-item"
                    onClick={(e) => {
                      e.preventDefault();
                      handleToggleOption(activeCategory, option);
                    }}
                  >
                    <div className={`bb-filters-checkbox-box ${isChecked ? 'checked' : ''}`}>
                      {isChecked && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <span className="bb-filters-option-label">{option.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bb-filters-footer">
          <button
            type="button"
            onClick={handleClear}
            style={{ background: 'none', border: 'none', color: '#64748B', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: '6px 8px' }}
          >
            Clear All
          </button>

          <button
            type="button"
            onClick={handleApplyCommit}
            style={{
              backgroundColor: '#E9001E',
              color: '#FFFFFF',
              border: 'none',
              padding: '10px 24px',
              borderRadius: 10,
              fontSize: 13.5,
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(233, 0, 30, 0.28)',
            }}
          >
            Apply ({draftFilters.length})
          </button>
        </div>
      </div>
    </div>
  );
}